import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Layers, Plus, Search, X } from "lucide-react";
import API from "../../../../../services/api";
import { v2CreateComponent, v2ListComponents } from "../../SalaryApiV2";
import { useToast } from "../../../../Context/ToastContext";
import {
  buildPayload,
  emptyForm,
  formIsValid,
  formulaStatus,
  isEarningSection,
  isDeductionSection,
} from "./salaryComponentModel";
import { listCatalogComponents } from "./pipelineComponentFilter";
import ComponentEditorForm from "./ComponentEditorForm";
import SalaryTemplatePipelineBar from "./SalaryTemplatePipelineBar";
import {
  PIPELINE_STEPS,
  pipelineStepPath,
  readPipelineState,
  writePipelineState,
} from "./salaryTemplatePipeline";
import { formulaOf } from "../../wizard/shared";
import "../../../../../styles/salary-template-pipeline.css";
import "../../../../../styles/salary-templates-premium.css";
import "../../../../../styles/salary-components-premium.css";

function componentMatches(comp, needle) {
  if (!needle) return true;
  const name = String(comp.name || "").toLowerCase();
  const code = String(comp.code || comp.system_code || "").toLowerCase();
  return name.includes(needle) || code.includes(needle);
}

function seedFormulasForAllocation(compRows, ids) {
  const state = readPipelineState();
  const nextFormulas = { ...state.formulas };
  for (const id of ids) {
    const comp = compRows.find((c) => String(c.component_id) === String(id));
    if (!comp) continue;
    if (!nextFormulas[id]?.expression) {
      nextFormulas[id] = { expression: formulaOf(comp) };
    }
  }
  return nextFormulas;
}

export default function SalaryTemplatePipelineAllocate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [extraWork, setExtraWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [form, setForm] = useState(() => emptyForm("earning"));
  const [submitted, setSubmitted] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [data, cfgRes] = await Promise.all([
          v2ListComponents(),
          API.get("/organisation/me/extra-work-config").catch(() => null),
        ]);
        const extra = cfgRes?.data?.extra_work || null;
        setExtraWork(extra);
        const visible = listCatalogComponents(data, extra);
        if (cancelled) return;
        setRows(visible);
        const state = readPipelineState();
        const fromState = (state.allocatedComponentIds || []).map(String);
        const valid = new Set(visible.map((r) => String(r.component_id)));
        setSelected(new Set(fromState.filter((id) => valid.has(id))));
        writePipelineState({ currentStep: PIPELINE_STEPS.components });
      } catch (e) {
        toast.error(e?.message || "Failed to load components");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const needle = query.trim().toLowerCase();
  const visibleRows = useMemo(() => {
    if (!needle) return rows;
    return rows.filter((c) => {
      const name = String(c.name || "").toLowerCase();
      const code = String(c.code || c.system_code || "").toLowerCase();
      return name.includes(needle) || code.includes(needle);
    });
  }, [rows, needle]);
  const earnings = useMemo(() => visibleRows.filter(isEarningSection), [visibleRows]);
  const deductions = useMemo(() => visibleRows.filter(isDeductionSection), [visibleRows]);
  const selectedCount = selected.size;

  function toggle(id) {
    const sid = String(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  }

  function resetCreateForm() {
    setForm(emptyForm("earning"));
    setSubmitted(false);
  }

  function openPicker() {
    setPickerQuery("");
    resetCreateForm();
    setPickerOpen(true);
  }

  const canCreate = formIsValid(form);

  async function handleCreate() {
    setSubmitted(true);
    if (!canCreate) {
      toast.error("Fill in all required fields before saving.");
      return;
    }
    const payload = buildPayload(form);
    const code = String(payload?.code || "").trim().toUpperCase();
    if (code === "OVERTIME" || code === "OT" || code === "ADVANCE_TAKEN") {
      toast.error("Use the system-defined Overtime and Advance Taken components instead of creating duplicates.");
      return;
    }
    setCreating(true);
    try {
      await createAndSelect(payload);
      resetCreateForm();
    } catch {
      /* createAndSelect already toasts */
    } finally {
      setCreating(false);
    }
  }

  const pickerNeedle = pickerQuery.trim().toLowerCase();
  const pickerRows = useMemo(() => {
    const list = rows.filter((c) => componentMatches(c, pickerNeedle));
    return list.sort((a, b) => {
      const groupA = isEarningSection(a) ? 0 : 1;
      const groupB = isEarningSection(b) ? 0 : 1;
      if (groupA !== groupB) return groupA - groupB;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
  }, [rows, pickerNeedle]);

  async function createAndSelect(payload) {
    try {
      const created = await v2CreateComponent(payload);
      const id = String(created?.component_id || "");
      if (!id) throw new Error("Component was not created");
      setRows((prev) => (prev.some((row) => String(row.component_id) === id) ? prev : [...prev, created]));
      const nextIds = [...new Set([...selectedRef.current, id].map(String))];
      setSelected(new Set(nextIds));
      const formulas = seedFormulasForAllocation([created], [id]);
      writePipelineState({
        allocatedComponentIds: nextIds,
        formulas,
        currentStep: PIPELINE_STEPS.components,
      });
      toast.success(`${created.name || "Component"} added to this salary structure.`);
      return created;
    } catch (e) {
      const detail = e?.detail || e?.message;
      toast.error(typeof detail === "string" ? detail : "Could not create component");
      throw e;
    }
  }

  function continueToFormulas() {
    const ids = [...selected];
    if (!ids.length) {
      toast.error("Select at least one earning or deduction component.");
      return;
    }
    setBusy(true);
    try {
      const formulas = seedFormulasForAllocation(rows, ids);
      const nextState = writePipelineState({
        allocatedComponentIds: ids,
        formulas,
        currentStep: PIPELINE_STEPS.formulas,
      });
      if (nextState.returnToReview) {
        writePipelineState({ returnToReview: false, currentStep: PIPELINE_STEPS.review });
        navigate(pipelineStepPath(PIPELINE_STEPS.review));
      } else {
        navigate(pipelineStepPath(PIPELINE_STEPS.formulas, nextState));
      }
      toast.success("Components saved in this structure draft.");
    } finally {
      setBusy(false);
    }
  }

  function renderBoard(kind, title, list) {
    const earning = kind === "earn";
    const picked = list.filter((c) => selected.has(String(c.component_id))).length;
    return (
      <section className={`stp-catalog-board ${earning ? "is-earn" : "is-ded"}`}>
        <header className="stp-catalog-board-head">
          <div>
            <h3>{title}</h3>
            <p>{list.length} component{list.length === 1 ? "" : "s"} · {picked} selected</p>
          </div>
        </header>
        {list.length ? (
          <div className="stp-catalog-cards">
            {list.map((c) => {
              const id = String(c.component_id);
              const on = selected.has(id);
              const missing = formulaStatus(c)?.tone === "missing";
              return (
                <label key={id} className={`stp-catalog-card${on ? " is-on" : ""}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(id)} />
                  <span className="stp-catalog-card-main">
                    <span className="stp-catalog-card-top">
                      <span className="stp-catalog-name">{c.name}</span>
                      <em className={earning ? "is-earn" : "is-ded"}>{earning ? "Earning" : "Deduction"}</em>
                    </span>
                    <code>{c.code || c.system_code || "—"}</code>
                    {missing ? <span className="stp-catalog-wait">Formula can be added later</span> : null}
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <p className="stp-catalog-empty">{needle ? "No components match this search." : "No components in this group yet."}</p>
        )}
      </section>
    );
  }

  return (
    <>
      <SalaryTemplatePipelineBar currentStep={PIPELINE_STEPS.components} />
      <section className="stp-card stp-catalog">
        <div className="stp-catalog-toolbar">
          <div className="stp-catalog-intro">
            <h2 className="stp-section-title">Choose components</h2>
            <p className="stp-section-hint">
              Every existing payroll component is listed here. Tick the ones this salary structure should use.
            </p>
          </div>
          <label className="stp-catalog-search">
            <Search size={16} />
            <input
              type="search"
              value={query}
              placeholder="Search by name or code"
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <span className="stp-catalog-count">{selectedCount} selected</span>
          <button
            type="button"
            className="stp-btn stp-btn-primary stp-alloc-add"
            disabled={loading}
            onClick={openPicker}
          >
            <Plus size={16} /> Add component
          </button>
        </div>
        {loading ? (
          <p className="stp-section-hint">Loading components…</p>
        ) : (
          <div className="stp-catalog-boards">
            {renderBoard("earn", "Earnings", earnings)}
            {renderBoard("ded", "Deductions", deductions)}
          </div>
        )}
        {pickerOpen
          ? createPortal(
              <div className="stp-add-page" role="dialog" aria-modal="true" aria-labelledby="stp-add-title">
                <div className="stp-add-page-card">
                  <div className="stp-add-page-split">
                  <section className="stp-add-page-form scp-card scp-editor-card">
                    <header className="scp-editor-header stp-add-editor-head">
                      <div className="scp-editor-header-main">
                        <span className="scp-modal-head-icon" aria-hidden>
                          <Plus size={18} />
                        </span>
                        <div>
                          <h2 id="stp-add-title" className="scp-editor-title">
                            <Layers size={20} className="scp-title-icon-inline" aria-hidden />
                            Add component
                          </h2>
                          <p className="scp-editor-subtitle">
                            Define code, category, calculation, and status in one place.
                          </p>
                        </div>
                      </div>
                      <button type="button" className="stp-icon-btn" onClick={() => setPickerOpen(false)} aria-label="Close">
                        <X size={18} />
                      </button>
                    </header>
                    <ComponentEditorForm
                      form={form}
                      setForm={setForm}
                      mode="create"
                      extraWork={extraWork}
                      onOpenExtraWork={() => navigate("/salary-v2/overtime")}
                      submitted={submitted}
                      showTools={false}
                    />
                    <footer className="scp-editor-footer">
                      <button type="button" className="bw-btn bw-btn-secondary" disabled={creating} onClick={resetCreateForm}>
                        Cancel
                      </button>
                      <button type="button" className="bw-btn bw-btn-primary" disabled={!canCreate || creating} onClick={handleCreate}>
                        {creating ? "Saving…" : "Create component"}
                      </button>
                    </footer>
                  </section>
                  <section className="stp-add-existing">
                    <div className="stp-add-existing-head">
                      <div>
                        <h3>Existing components</h3>
                        <p>Tick the ones this salary structure should use.</p>
                      </div>
                      <span className="stp-catalog-count">{pickerRows.length} shown</span>
                    </div>
                    <label className="stp-add-page-search">
                      <Search size={16} />
                      <input
                        type="search"
                        value={pickerQuery}
                        placeholder="Search by name or code"
                        onChange={(e) => setPickerQuery(e.target.value)}
                      />
                    </label>
                  <div className="stp-add-page-list">
                    {pickerRows.length ? (
                      pickerRows.map((c) => {
                        const id = String(c.component_id);
                        const on = selected.has(id);
                        const earning = isEarningSection(c);
                        return (
                          <label key={id} className={`stp-add-item${on ? " is-on" : ""}`}>
                            <input type="checkbox" checked={on} onChange={() => toggle(id)} />
                            <span className="stp-add-item-text">
                              <span className="stp-add-item-name">{c.name}</span>
                              <span className="stp-add-item-code">{c.code || c.system_code || "—"}</span>
                            </span>
                            <em className={earning ? "is-earn" : "is-ded"}>{earning ? "Earning" : "Deduction"}</em>
                          </label>
                        );
                      })
                    ) : (
                      <p className="stp-catalog-empty">No existing component matches this search.</p>
                    )}
                  </div>
                  </section>
                  </div>
                  <footer className="stp-add-page-foot">
                    <button type="button" className="stp-btn stp-btn-primary" onClick={() => setPickerOpen(false)}>
                      Done
                    </button>
                  </footer>
                </div>
              </div>,
              document.body,
            )
          : null}
        <div className="stp-pipeline-sticky">
          <button type="button" className="stp-btn stp-btn-ghost" onClick={() => navigate(pipelineStepPath(PIPELINE_STEPS.template))}>
            Back
          </button>
          <button type="button" className="stp-btn stp-btn-primary" disabled={busy || loading} onClick={continueToFormulas}>
            {busy ? "Continuing…" : "Next: Configure Formulas →"}
            {!busy ? <ArrowRight size={16} /> : null}
          </button>
        </div>
      </section>
    </>
  );
}
